import {
  Check as CheckIcon,
  ClearAll as ClearAllIcon,
  Error as ErrorIcon,
  Factory as FactoryIcon,
  Info as InfoIcon,
  Inventory as InventoryIcon,
  AttachMoney as MoneyIcon,
  Notifications as NotificationsIcon,
  ShoppingCart as OrderIcon,
  Person as PersonIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import api from '../../services/api';
import { logError } from '../../utils/errorLogger';

const NotificationCenter = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const { t, localeCode } = useI18n();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  // Get icon based on notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order':
        return <OrderIcon color="primary" />;
      case 'inventory':
        return <InventoryIcon color="warning" />;
      case 'payment':
        return <MoneyIcon color="success" />;
      case 'user':
        return <PersonIcon color="info" />;
      case 'shipping':
        return <ShippingIcon color="primary" />;
      case 'manufacturing':
        return <FactoryIcon color="secondary" />;
      case 'warning':
        return <WarningIcon color="warning" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'success':
        return <SuccessIcon color="success" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    // Less than 1 hour
    if (diff < 3600000) {
      const minutes = Math.floor(diff / 60000);
      return t('notif.minutesAgo').replace('{{m}}', minutes);
    }

    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return t('notif.hoursAgo').replace('{{h}}', hours);
    }

    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return t('notif.daysAgo').replace('{{d}}', days);
    }

    return date.toLocaleDateString(localeCode);
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const { data } = await api.get('/notifications', {
        params: {
          user_id: user.id,
          page_size: 20,
        },
      });
      setNotifications(data.data || data.items || []);
    } catch (error) {
      logError(error, { context: 'NotificationCenter.fetchNotifications' });
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Fetch unread count (silent — no console.error spam)
  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    try {
      const { data } = await api.get('/notifications/unread/count', {
        params: { user_id: user.id },
        timeout: 10000,
      });
      setUnreadCount(data.count || 0);
    } catch {
      // Silent fail — transient network errors are expected
    }
  }, [user?.id]);

  // Initial fetch and polling (with visibility-aware restart)
  useEffect(() => {
    fetchUnreadCount();

    // Poll every 60 seconds
    const interval = setInterval(fetchUnreadCount, 60000);

    // Refetch when tab becomes visible again
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchUnreadCount]);

  // Fetch notifications when menu opens
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  // Open menu
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      logError(error, { context: 'NotificationCenter.markAsRead' });
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      await api.post('/notifications/read-all', null, {
        params: { user_id: user.id },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (error) {
      logError(error, { context: 'NotificationCenter.markAllAsRead' });
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate based on notification type
    if (notification.link) {
      navigate(notification.link);
      handleClose();
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={t('common.notifications')}
        aria-controls={open ? 'notifications-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        sx={{ minWidth: 44, minHeight: 44 }}
      >
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : null}
          color="error"
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        id="notifications-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 360,
            maxHeight: 480,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1}
        >
          <Typography variant="h6">{t('notif.title')}</Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<ClearAllIcon />}
              onClick={markAllAsRead}
            >
              {t('notif.readAll')}
            </Button>
          )}
        </Box>

        <Divider />

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box py={4} textAlign="center">
            <NotificationsIcon
              sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
            />
            <Typography color="text.secondary">{t('notif.empty')}</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  button
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 1.5,
                    bgcolor: notification.is_read
                      ? 'transparent'
                      : 'action.hover',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight={notification.is_read ? 'normal' : 'bold'}
                          sx={{
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.is_read && (
                          <Chip
                            label={t('notif.statusNew')}
                            size="small"
                            color="primary"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="div"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          component="div"
                        >
                          {formatDate(notification.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                  {!notification.is_read && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(notification.id);
                      }}
                      aria-label={t('common.markAsRead')}
                      sx={{ minWidth: 44, minHeight: 44 }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        <Divider />

        <Box p={1}>
          <Button
            fullWidth
            onClick={() => {
              navigate('/notifications');
              handleClose();
            }}
          >
            {t('notif.allNotifications')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationCenter;
